import { Head, Link, router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { PageHeader } from "@/components/layouts/PageHeader"
import { StatusBadge } from "@/components/StatusBadge"
import { JsonViewer } from "@/components/ui/json-viewer"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { ArrowLeft, RefreshCw, AlertTriangle, Trash2, ChevronDown } from "lucide-react"
import { useState } from "react"
import { formatDateTime, useTimezone } from "@/lib/date"
import type { Capture, CaptureStatus } from "@/types"

interface CaptureShowProps {
  capture: Capture
}

export default function Show({ capture }: CaptureShowProps) {
  const timezone = useTimezone()
  const [showFullContent, setShowFullContent] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const contentPreviewLength = 500
  const hasLongContent = (capture.content?.length ?? 0) > contentPreviewLength

  function handleRetry() {
    router.post(`/admin/captures/${capture.id}/retry_delivery`, {}, { preserveScroll: true })
  }

  function handleRedeliver() {
    router.post(`/admin/captures/${capture.id}/retry_delivery`, {}, { preserveScroll: true })
  }

  const title = capture.title || `Capture #${capture.id}`

  return (
    <>
      <Head title={title} />
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon-sm" asChild>
            <Link href="/admin/captures">
              <ArrowLeft className="size-4" />
            </Link>
          </Button>
          <div className="flex-1">
            <PageHeader title={title}>
              {(capture.status === "pending" || capture.status === "failed") && (
                <Button variant="outline" onClick={handleRetry}>
                  <RefreshCw className="size-4" />
                  Retry Delivery
                </Button>
              )}
              {capture.status === "delivered" && (
                <Button variant="outline" onClick={handleRedeliver}>
                  <RefreshCw className="size-4" />
                  Re-deliver
                </Button>
              )}
            </PageHeader>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex cursor-pointer items-center gap-1">
                <StatusBadge status={capture.status} />
                <ChevronDown className="text-muted-foreground size-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {(["pending", "delivered", "failed"] as CaptureStatus[])
                .filter((s) => s !== capture.status)
                .map((status) => (
                  <DropdownMenuItem
                    key={status}
                    onClick={() =>
                      router.patch(
                        `/admin/captures/${capture.id}/update_status`,
                        { status },
                        { preserveScroll: true }
                      )
                    }
                  >
                    Mark as {status}
                  </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {capture.status === "failed" && capture.error_message && (
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertDescription>{capture.error_message}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-muted-foreground text-sm">Source</dt>
                <dd>
                  {capture.source_name ? (
                    <Link
                      href={`/admin/sources/${capture.source_id}`}
                      className="font-medium hover:underline"
                    >
                      {capture.source_name}
                    </Link>
                  ) : (
                    <span className="font-medium">Source #{capture.source_id}</span>
                  )}
                </dd>
              </div>
              {capture.external_uid && (
                <div>
                  <dt className="text-muted-foreground text-sm">External UID</dt>
                  <dd className="font-mono text-sm">{capture.external_uid}</dd>
                </div>
              )}
              <div>
                <dt className="text-muted-foreground text-sm">Captured At</dt>
                <dd>{formatDateTime(capture.captured_at, timezone)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-sm">Delivered At</dt>
                <dd>{formatDateTime(capture.delivered_at, timezone)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-sm">Retry Count</dt>
                <dd>{capture.retry_count}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <Tabs defaultValue="content">
          <TabsList>
            <TabsTrigger value="content">Content</TabsTrigger>
            <TabsTrigger value="extracted">Extracted Data</TabsTrigger>
            <TabsTrigger value="delivered">Delivered Payload</TabsTrigger>
            <TabsTrigger value="raw">Raw Payload</TabsTrigger>
            <TabsTrigger value="response">Delivery Response</TabsTrigger>
          </TabsList>

          <TabsContent value="content" className="mt-4">
            <Card>
              <CardContent className="pt-6">
                {capture.content ? (
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <p className="whitespace-pre-wrap">
                      {showFullContent || !hasLongContent
                        ? capture.content
                        : `${capture.content.slice(0, contentPreviewLength)}...`}
                    </p>
                    {hasLongContent && (
                      <Button
                        variant="link"
                        size="sm"
                        className="mt-2 p-0"
                        onClick={() => setShowFullContent(!showFullContent)}
                      >
                        {showFullContent ? "Show less" : "Show more"}
                      </Button>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">No content</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="extracted" className="mt-4">
            <Card>
              <CardContent className="pt-6">
                <JsonViewer data={capture.extracted_data} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="delivered" className="mt-4">
            <Card>
              <CardContent className="pt-6">
                {capture.delivered_payload ? (
                  <JsonViewer data={capture.delivered_payload} />
                ) : (
                  <p className="text-muted-foreground text-sm">Not yet delivered</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="raw" className="mt-4">
            <Card>
              <CardContent className="pt-6">
                <JsonViewer data={capture.raw_payload} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="response" className="mt-4">
            <Card>
              <CardContent className="pt-6">
                {capture.delivery_response ? (
                  <div className="bg-muted overflow-auto rounded-md p-4" style={{ maxHeight: "400px" }}>
                    <pre className="font-mono text-sm whitespace-pre-wrap">
                      {capture.delivery_response}
                    </pre>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">No delivery response</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="flex items-center">
          <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
            <DialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 className="size-4" />
                Delete Capture
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Capture</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete this capture? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDeleteOpen(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => router.delete(`/admin/captures/${capture.id}`)}
                >
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </>
  )
}
